import React, { useState, useRef, useEffect } from "react";
import "./toggleSlider.scss";

const ToggleSlider = ({
  leftLabel = "",
  rightLabel = "",
  defaultPosition = "right",
  handleChangeSlider,
  activeContent = "ON",
  inactiveContent = "OFF",
  sliderWrapperStyle = {},
  sliderTextStyle = {},
  className = "",
  checked,
}) => {
  const [active, setActive] = useState(defaultPosition === "right");
  const sliderRef = useRef(null);
  const toggleRef = useRef(null);
  const isDragging = useRef(false);
  const [posX, setPosX] = useState(active ? 47 : 0); // Initial position

  // We'll store references to local pointer event handlers so we can remove them
  const pointerMoveHandler = useRef(null);
  const pointerUpHandler = useRef(null);

  // Helper function to clamp position
  const clamp = (min, value, max) => Math.max(min, Math.min(value, max));

  // Called when user presses down on the toggle
  const handlePointerDown = (e) => {
    e.preventDefault();
    if (!sliderRef.current || !toggleRef.current) return;

    isDragging.current = true;

    // Add pointermove & pointerup to the same element or the document
    // but only while dragging, then remove them after.
    pointerMoveHandler.current = handlePointerMove;
    pointerUpHandler.current = handlePointerUp;

    document.addEventListener("pointermove", pointerMoveHandler.current);
    document.addEventListener("pointerup", pointerUpHandler.current);
  };

  // Move handler
  const handlePointerMove = (e) => {
    if (!isDragging.current || !sliderRef.current || !toggleRef.current) return;

    const sliderBounds = sliderRef.current.getBoundingClientRect();
    const pointerX = e.clientX;

    // Calculate and clamp position
    const pos = pointerX - sliderBounds.left - toggleRef.current.offsetWidth / 2;
    const clampedPos = clamp(
      0,
      pos,
      sliderBounds.width - toggleRef.current.offsetWidth
    );

    setPosX(clampedPos);

    // Dynamically update the `active` state during drag
    const sliderWidth = sliderBounds.width;
    const threshold = sliderWidth / 2;

    if (clampedPos >= threshold - 20) {
      setActive(true);
    } else {
      setActive(false);
    }
  };

  // Up handler
  const handlePointerUp = () => {
    if (!isDragging.current) return;

    isDragging.current = false;
    // Remove the event listeners once done dragging
    document.removeEventListener("pointermove", pointerMoveHandler.current);
    document.removeEventListener("pointerup", pointerUpHandler.current);

    pointerMoveHandler.current = null;
    pointerUpHandler.current = null;

    // Snap to ON/OFF based on final position
    if (!sliderRef.current) return;
    const sliderWidth = sliderRef.current.offsetWidth;
    const threshold = sliderWidth / 2;

    if (posX >= threshold - 20) {
      setActive(true);
      setPosX(47); // Right position
      if (handleChangeSlider) handleChangeSlider("right"); 
    } else {
      setActive(false);
      setPosX(0); // Left position
      if (handleChangeSlider) handleChangeSlider("left");
    }
  };

  // Sync internal state with the prop when it changes
  useEffect(() => {
    setActive(checked);
    setPosX(checked ? 47 : 0);
  }, [checked]);

  return (
    <div className={`slider-wrapper ${className}`} style={sliderWrapperStyle}>
      <span className="slider-label" style={{ fontWeight: "bold" }}>
        Auto Pay Slider
      </span>
      <div>
        <label className="checkbox-slider" ref={sliderRef}>
          <span style={sliderTextStyle}>{active ? rightLabel : leftLabel}</span>
          <input
            type="checkbox"
            checked={active}
            readOnly
            aria-checked={active}
          />
          <div className={`inner ${active ? "active" : ""}`}>
            <div
              className="toggle"
              style={{ left: `${posX}px` }}
              data-content={active ? activeContent : inactiveContent}
              onPointerDown={handlePointerDown}
              ref={toggleRef}
            />
          </div>
        </label>
      </div>
    </div>
  );
};

export default ToggleSlider;

// No Global Listeners on Mount:
// We only add pointermove and pointerup listeners inside 
// handlePointerDown, and remove them in handlePointerUp. 
// This ensures we don’t capture pointer up events from other buttons.

// e.preventDefault() is called only when the user drags the toggle, 
// preventing text selection or other default behaviors.

// When Drag Ends (handlePointerUp):
// We remove the listeners, so clicking “Edit” or “Delete” 
// elsewhere in the document won’t trigger handlePointerUp.

// No Overlap with other Buttons:
// Because the event is only added while dragging, normal 
// clicks on “Edit” or “Delete” won't call the toggle’s pointer up logic.

// Validate the Behavior
// Press "Edit" or "Delete":
// The pointer up event is not captured by the toggle, so no autopay logic runs.

// Drag the Toggle:
// The pointer up event ends the drag and sets the new position (ON or OFF).

// Conclusion
// This refactored ToggleSlider ensures it only reacts to 
//pointer events while the user is dragging the slider. 
// Therefore, pressing other buttons (Edit/Delete) won’t 
// trigger the toggle’s pointer up event. You can now 
// uncomment your <ToggleSlider /> usage in CampaignMetricsCard, 
// and you should no longer see the autopay logic invoked for other buttons.
